import React, { useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { PopupTransition } from 'components/@extended/Transitions';
import { FormattedMessage } from 'react-intl';
import { useFormikContext } from 'formik';
import { UpdateConfigDialogFormValues } from '../../update-config-dialog/dto';
import Filter from '../filter';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { CustomFilter } from 'inside-fifa/api/generic-tables/dto';
import { FilterOption } from '../filter/dto';
import { dateFormats, filterTypes, viewFormats } from './consts';

const ShowFilters: React.FC<{ languagesUsed: string[] }> = (props) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [currentFilterIndex, setCurrentFilterIndex] = useState<number | null>(null);
    const { values, setFieldValue } = useFormikContext<UpdateConfigDialogFormValues>();

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue('config.filters.isSearchBoxEnabled', event.target.checked);
    };

    const handleDialogClose = () => {
        setIsModalOpen(false);

        setCurrentFilterIndex(null);
    };

    const handleAddFilterClick = () => {
        setCurrentFilterIndex(null);
        setIsModalOpen(true);
    };

    const handleEditFilterClick = (index: number) => {
        setCurrentFilterIndex(index);
        setIsModalOpen(true);
    };

    const handleDeleteFilterClick = (index: number) => {
        const updatedFilters = values.config.filters.customFilters.filter((_, i) => i !== index);
        setFieldValue('config.filters.customFilters', updatedFilters);
    };

    const handleSaveFilter = (newFilter: CustomFilter) => {
        const updatedFilters = [...values.config.filters.customFilters];
        if (currentFilterIndex !== null) {
            updatedFilters[currentFilterIndex] = newFilter;
        } else {
            updatedFilters.push(newFilter);
        }
        setFieldValue('config.filters.customFilters', updatedFilters);
        setIsModalOpen(false);
    };

    const getPlaceholderByKey = <T,>(items: FilterOption<T>[], key: T): JSX.Element | '-' => {
        const item = items.find((item) => item.key === key);
        return item ? item.placeholder : '-';
    };

    return (
        <>
            <Stack direction="column" sx={{ p: 2 }}>
                <Box>
                    <Checkbox
                        aria-label="Enable search input"
                        checked={values.config.filters.isSearchBoxEnabled}
                        onChange={handleCheckboxChange}
                    />
                    <FormattedMessage id="inside-fifa.generic-tables.filters.enable-search-input" />
                </Box>
                <Box>
                    <Button role="button" variant="outlined" color="primary" onClick={handleAddFilterClick}>
                        <FormattedMessage id="inside-fifa.generic-tables.filters.add-filter" />
                    </Button>
                </Box>
                <Box>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <FormattedMessage id="inside-fifa.id" />
                                    </TableCell>
                                    <TableCell>
                                        <FormattedMessage id="inside-fifa.generic-tables.filters.label" />
                                    </TableCell>
                                    <TableCell>
                                        <FormattedMessage id="inside-fifa.generic-tables.filters.data-column" />
                                    </TableCell>
                                    <TableCell>
                                        <FormattedMessage id="inside-fifa.generic-tables.column-type" />
                                    </TableCell>
                                    <TableCell>
                                        <FormattedMessage id="inside-fifa.generic-tables.filters.view" />
                                    </TableCell>
                                    <TableCell>
                                        <FormattedMessage id="inside-fifa.generic-tables.filters.format" />
                                    </TableCell>
                                    <TableCell>
                                        <FormattedMessage id="inside-fifa.generic-tables.filters.show-outside" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <FormattedMessage id="inside-fifa.actions-header" />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {values.config.filters.customFilters?.map((filter, index) => (
                                    <TableRow key={`${JSON.stringify(filter)}_${index}`}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{filter.label?.['en']}</TableCell>
                                        <TableCell>{filter.dataColumn}</TableCell>

                                        <TableCell>{getPlaceholderByKey(filterTypes, filter.type)}</TableCell>
                                        <TableCell>
                                            {filter.view ? getPlaceholderByKey(viewFormats, filter.view) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {filter.format ? getPlaceholderByKey(dateFormats, filter.format) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {filter.shouldShowOutsidePanel ? (
                                                <FormattedMessage id="inside-fifa.yes" />
                                            ) : (
                                                <FormattedMessage id="inside-fifa.no" />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                edge="end"
                                                aria-label="edit"
                                                onClick={() => handleEditFilterClick(index)}
                                            >
                                                <EditOutlined />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() => handleDeleteFilterClick(index)}
                                            >
                                                <DeleteOutlined />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Stack>
            <Dialog
                maxWidth="sm"
                TransitionComponent={PopupTransition}
                fullWidth
                open={isModalOpen}
                onClose={handleDialogClose}
                sx={{ '& .MuiDialog-paper': { p: 0 } }}
            >
                <Filter
                    filterData={
                        currentFilterIndex !== null ? values.config.filters.customFilters[currentFilterIndex] : null
                    }
                    onSaveOrEdit={handleSaveFilter}
                    onCancel={handleDialogClose}
                    columns={values.config.columns.map((column) => ({ key: column.key, title: column.title }))}
                    dateFormats={dateFormats}
                    viewFormats={viewFormats}
                    filterTypes={filterTypes}
                    languagesUsed={props.languagesUsed}
                />
            </Dialog>
        </>
    );
};

export default ShowFilters;
